// ============================================================
// SETTINGS VIEW — render settings page, tag settings, panels
// ============================================================
import { clients, visibleColumns, currentView } from './state.js';
import { escHtml, formatCurrency, showToast, getEndDate, getMonthsAsClient, isRenewalNeeded, upgradeSelects, syncUpgradedSelects, showConfirm, continuityRateOf } from './utils.js';
import { loadSettings, saveSettingsToStorage, getCurrentMode, getAutoTheme,
         loadTagDefs, saveTagDefs, getTagStyle,
         applyLogoFromStorage, applyTheme, applyCustomColors,
         COLUMNS, LEAD_STAGES,
         loadColumnPrefs, saveColumnPrefs,
         DEFAULT_COLORS, CURRENCIES, DATE_FORMATS,
         localToday, localDateStr, getCoachTimezone, getSupabase, refreshEmailDomainStatus } from './config.js';
import { callFunction } from './api.js';
import { loadQuickReplies, saveQuickReplies, LOAD_FAILED, SAVE_FAILED } from './messages/quick-replies.js';
// Task 15 — Atlas's own voice switch is per-browser localStorage (Task 12),
// not a coaches.settings field, so it is read/written straight through
// these two functions rather than folded into the loadSettings() blob.
import { speakEnabled, setSpeakEnabled } from './jarvis/result.js';
import { renderCancelAgreement, DEFAULT_CANCEL_AGREEMENT_TEMPLATE, useChangeBillingParagraph } from './cancel-agreement-language.js';
import { buildCancelAddress, isBrandedCancelAddress } from './cancel-address.js';
import { applySidebarCollapsed, savedSidebarCollapsed } from './side-menus.js';
import { renderTimeZoneSetting } from './time-zone-setting.js';
import { loadCancellationSettings } from './cancellation-settings.js';
import { fillClientTeasersSwitch } from './client-app-tabs-settings.js';
import { initAvailabilitySets } from './availability-sets.js';

// Forward declarations
let _renderClientsTable = null;
let _renderDashboard = null;
export function setSettingsCallbacks({ renderClientsTable, renderDashboard }) {
  _renderClientsTable = renderClientsTable;
  _renderDashboard = renderDashboard;
}

// Retained as a no-op so existing imports/callers keep working after the
// sidebar mode badge + Stripe Mode toggle were removed. All coach-facing
// operations run in live mode. Safe to delete along with its callers
// in a later cleanup pass.
export function updateModeBadge() {}

// Platform-level client-tab availability (admin layer), fetched once + cached.
// Used to hide coach-settings toggles for tabs the platform hasn't enabled yet.
let _platformTabAvail = null;
async function ensurePlatformTabAvailability() {
  if (_platformTabAvail) return _platformTabAvail;
  try {
    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;
    const res = await fetch('/.netlify/functions/coach-tab-availability', {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (res.ok) _platformTabAvail = (await res.json()).availability || null;
  } catch { /* leave null → no rows hidden (safe default) */ }
  return _platformTabAvail;
}
function applyPlatformTabAvailability(avail) {
  if (!avail) return;
  for (const t of ['calendar', 'nutrition', 'checkins', 'sessions', 'courses', 'community', 'progress']) {
    const row = document.getElementById('s-tab-' + t)?.closest('.settings-toggle-row');
    if (row) row.style.display = avail[t] === false ? 'none' : '';
  }
}

export function renderSettings() {
  const settings = loadSettings();

  const themeTitle = document.getElementById('theme-toggle-title');
  const themeSub   = document.getElementById('theme-toggle-sub');
  const t = settings.theme || 'dark';
  document.querySelectorAll('.theme-seg-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.themeVal === t);
  });
  if (t === 'auto') {
    const resolved = getAutoTheme();
    if (themeTitle) themeTitle.textContent = 'Auto Mode';
    if (themeSub)   themeSub.textContent   = resolved === 'light' ? 'Currently light · switches at 7pm' : 'Currently dark · switches at 7am';
  } else if (t === 'light') {
    if (themeTitle) themeTitle.textContent = 'Light Mode';
    if (themeSub)   themeSub.textContent   = 'Clean and bright display';
  } else {
    if (themeTitle) themeTitle.textContent = 'Dark Mode';
    if (themeSub)   themeSub.textContent   = 'Easy on the eyes in low light';
  }


  const cc = settings.customColors || {};
  const setColorPicker = (key, val) => {
    const v = (val || '').toLowerCase();
    const picker = document.getElementById(`color-${key}`);
    const hex    = document.getElementById(`color-${key}-hex`);
    if (picker) picker.value = v;
    if (hex)    hex.value    = v.toUpperCase();
  };
  setColorPicker('brand',  cc.brand  || DEFAULT_COLORS.brand);
  setColorPicker('chart1', cc.chart1 || DEFAULT_COLORS.chart1);

  // Logo — settings field
  applyLogoFromStorage();

  // Email Notifications
  // The Booking links card is drawn by the Your domain card, from the same plan.
  renderYourDomainCard();
  const emailBiz = document.getElementById('s-email-business-name');
  const emailReply = document.getElementById('s-email-reply-to');
  const emailReceipts = document.getElementById('s-email-send-receipts');
  const emailFailure = document.getElementById('s-email-send-failure');
  if (emailBiz) emailBiz.value = settings.emailBusinessName || '';
  if (emailReply) emailReply.value = settings.emailReplyTo || '';
  if (emailReceipts) emailReceipts.checked = !!settings.emailSendReceipts;
  if (emailFailure) emailFailure.checked = !!settings.emailSendFailureNotice;

  // Client Receipt Branding (Payments tab)
  const rbLogo    = document.getElementById('s-receipt-logo-url');
  const rbAccent  = document.getElementById('s-receipt-accent-color');
  const rbSupport = document.getElementById('s-receipt-support-email');
  const rbFooter  = document.getElementById('s-receipt-footer-text');
  if (rbLogo)    rbLogo.value    = settings.receiptLogoUrl || '';
  if (rbAccent)  rbAccent.value  = settings.receiptAccentColor || '#7c3aed';
  if (rbSupport) rbSupport.value = settings.receiptSupportEmail || '';
  if (rbFooter)  rbFooter.value  = settings.receiptFooterText || '';

  // White-label the "who sends the receipt" label with the coach's business name.
  const bizName = settings.emailBusinessName || settings.businessName || '';
  const platLabel = document.getElementById('s-receipt-source-platform-label');
  if (platLabel) platLabel.textContent = bizName
    ? `${bizName} sends the receipt (recommended)`
    : 'You send the receipt (recommended)';

  // Receipt logo — preview + which buttons show. "Use my brand logo" appears
  // only when an http(s) brand logo exists (a data: URI won't render in email).
  const curLogo = settings.receiptLogoUrl || '';
  const rbLogoPreview = document.getElementById('s-receipt-logo-preview');
  const rbLogoRemove  = document.getElementById('btn-receipt-logo-remove');
  const rbLogoBrand   = document.getElementById('btn-receipt-logo-usebrand');
  if (rbLogoPreview) { rbLogoPreview.src = curLogo; rbLogoPreview.style.display = curLogo ? '' : 'none'; }
  if (rbLogoRemove)  rbLogoRemove.classList.toggle('hidden', !curLogo);
  const brandLogo = localStorage.getItem('revfit_logo_full') || localStorage.getItem('revfit_logo_full_light') || localStorage.getItem('revfit_logo') || '';
  // http only (data: URIs and SVG don't render across email apps).
  const brandLogoUsable = brandLogo.startsWith('http') && brandLogo !== curLogo && !brandLogo.split('?')[0].toLowerCase().endsWith('.svg');
  if (rbLogoBrand) rbLogoBrand.classList.toggle('hidden', !brandLogoUsable);
  // Receipt delivery — 'platform' | 'stripe', default 'platform' when absent
  const rbSource = settings.receiptSource === 'stripe' ? 'stripe' : 'platform';
  document.querySelectorAll('input[name="s-receipt-source"]').forEach(r => { r.checked = r.value === rbSource; });
  document.getElementById('receipt-source-note-platform')?.classList.toggle('hidden', rbSource !== 'platform');
  document.getElementById('receipt-source-note-stripe')?.classList.toggle('hidden', rbSource !== 'stripe');

  renderTagSettings();
  renderTimeZoneSetting().catch((e) => console.warn('[settings] time zone card', e));

  const intensityEl = document.getElementById('s-intensity-metric');
  if (intensityEl) intensityEl.value = settings.intensityMetric === 'rpe' ? 'rpe' : 'rir';

  // Form check videos — defaultClientMode + weeklyCap (gate semantics in
  // netlify/functions/_shared/form-video-gate.js; inherit-by-omission).
  const fvCfg = settings.formCheckVideo || {};
  const fvModeEl = document.getElementById('s-form-video-mode');
  if (fvModeEl) fvModeEl.value = ['on', 'request_only', 'off'].includes(fvCfg.defaultClientMode) ? fvCfg.defaultClientMode : 'on';
  const fvCapEl = document.getElementById('s-form-video-cap');
  if (fvCapEl) fvCapEl.value = Number.isFinite(fvCfg.weeklyCap) ? fvCfg.weeklyCap : 3;

  // Client app tabs — which toggleable bottom tabs the coach shows clients.
  // Undefined = not customized → show all (every box checked). The server
  // intersects this with platform availability (see _shared/tab-config.js).
  // No 'workouts' row: the client app's Workouts tab was removed (2026-08-03).
  // A program is now read from a workout's overview -> "See the full program".
  // The server's TOGGLEABLE_TABS still lists it so already-stored coach
  // settings stay valid; nothing renders it.
  const tabKeys = ['calendar', 'nutrition', 'checkins', 'sessions', 'courses', 'community', 'progress'];
  const enabledTabs = Array.isArray(settings.enabledTabs) ? settings.enabledTabs : null;
  for (const t of tabKeys) {
    const el = document.getElementById('s-tab-' + t);
    if (el) el.checked = enabledTabs ? enabledTabs.includes(t) : true;
  }
  // The client teasers switch under them (on unless saved off).
  fillClientTeasersSwitch(settings);
  // Hide tabs the platform hasn't enabled yet (e.g. Courses) so the coach can't
  // toggle something their clients can never see. Async — applies when the
  // resolved availability arrives (cached after the first load).
  ensurePlatformTabAvailability().then(applyPlatformTabAvailability);

  const protoEl = document.getElementById('s-protocols-enabled');
  if (protoEl) protoEl.checked = !!settings.protocolsEnabled;

  // ⚠️ OFF BY DEFAULT, PER COACH — and per coach is the half that was wrong
  // until the final fix round. A coach can whisper a command with a client
  // sitting across the desk; the platform announcing "Tom's session" back at
  // conversational volume is a privacy problem speaking into it never had.
  // speakEnabled()/setSpeakEnabled() (modules/jarvis/result.js) are still the
  // only source of truth here — no Save button, applies the moment the switch
  // moves, same as the theme toggle — but they now ride `revfit_settings`,
  // which config.js re-seeds per signed-in person, rather than a localStorage
  // key of their own that survived a coach switch.
  //
  // ⚠️ RULING J83 — TWO CHECKBOXES, ONE FUNCTION. A team member cannot open
  // this tab (Appearance is owner-only), so the identical card lives on the
  // Security tab instead (index.html, `data-member-only`) — reachable by
  // every seat. Both checkboxes are bound through this one function so they
  // can never drift out of sync with each other or with speakEnabled().
  const bindAtlasSpeakToggle = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.checked = speakEnabled();
    el.onchange = () => setSpeakEnabled(el.checked);
  };
  bindAtlasSpeakToggle('s-atlas-speak-enabled');
  bindAtlasSpeakToggle('s-atlas-speak-enabled-member');

  // ⚠️ THE ONE SETTING IN THIS FILE THAT ASKS BEFORE IT TAKES EFFECT (brief W2
  // item 3, Ruling J148). Every other switch here changes something the coach
  // can see; this one turns a microphone on continuously for as long as the tab
  // is open. The analysis happens on their own machine and nothing is sent
  // anywhere until the word is heard — but "a microphone is now live in your
  // office" is not a thing to discover, so it is stated in one plain sentence
  // and the switch does not move until they say yes.
  //
  // ⚠️ AND THE SWITCH GOES BACK BY ITSELF IF THEY SAY NO. A checkbox left ticked
  // over a setting that was never saved is the worst of both: it reads as on and
  // behaves as off.
  //
  // ⚠️ EVERY SEAT, SINCE W4. It was owner-only while the privacy paragraph was
  // unwritten; the owner approved it himself on 2026-09-17 and it is published
  // on privacy.html, so the switch now sits on Appearance for the owner AND on
  // Security for a team member — the same two-checkboxes-one-function shape the
  // voice switch above uses (Ruling J83), so two copies of one value can never
  // drift apart on screen.
  const bindAtlasWakeToggle = (id) => {
    const wakeEl = /** @type {HTMLInputElement|null} */ (document.getElementById(id));
    if (!wakeEl) return;
    import('./jarvis/wake-controller.js').then(({ wakeEnabled }) => { wakeEl.checked = wakeEnabled(); }).catch(() => {});
    wakeEl.onchange = async () => {
      const { setWakeEnabled } = await import('./jarvis/wake-controller.js');
      if (!wakeEl.checked) { setWakeEnabled(false); return; }
      const ok = await showConfirm(
        'Atlas will listen on this device for the word "Atlas" whenever this tab is open. '
        + 'Nothing is sent anywhere until it hears it. Turn off any time.',
        { okLabel: 'Turn it on' },
      );
      if (!ok) { wakeEl.checked = false; return; }

      // ⚠️ A SEAT WITH NO ATLAS MUST NOT ARM A MICROPHONE (W4b). W4 opened this
      // switch to every seat, which is right — but a team member the owner has
      // not given Atlas to could then turn a live microphone on for something
      // that will refuse every command they speak into it. Nothing leaks (the
      // wake engine is local and needs no seat) and nothing breaks; it is simply
      // not a state to leave available.
      //
      // ⚠️ ASKED OF THE SERVER, NOT DECIDED HERE. The mint endpoint already
      // answers this — 402 is "no seat", the same rule seat-entitlement.js
      // enforces everywhere — so there is no new field, no query on anybody's
      // boot, and no copy of the entitlement rule in the browser to drift. The
      // account owner is never refused because hasSeat() answers true for them,
      // which is why there is no owner branch here either.
      //
      // ⚠️ AND ONLY A 402 REFUSES. A rate limit, a 500, a dropped connection or
      // an import that throws are all "not now", not "not yours" — a setting
      // that would not save because Netlify was slow is a worse defect than the
      // one this closes. Everything else arms exactly as it did yesterday, and
      // the command path reports real failures when they really happen.
      let seat = { ok: true, status: 200 };
      try {
        const { checkSttSeat } = await import('./jarvis/stt-token.js');
        seat = await checkSttSeat();
      } catch { /* the module would not load; that is not a billing answer */ }
      if (!seat.ok && seat.status === 402) {
        wakeEl.checked = false;
        showToast('Atlas isn\'t part of your seat yet. Ask the account owner to add it under Team.', 'error');
        return;
      }

      setWakeEnabled(true);
      // Arm it now rather than on the next reload — the coach just said yes and
      // expects it to be listening.
      const { armWake } = await import('./jarvis/wake-controller.js');
      const { wakeOpener } = await import('./jarvis/index.js');
      await armWake({ onWake: wakeOpener() });
      const { mountWakeIndicator } = await import('./jarvis/wake-indicator.js');
      mountWakeIndicator();
    };
  };
  bindAtlasWakeToggle('s-atlas-wake-enabled');
  bindAtlasWakeToggle('s-atlas-wake-enabled-member');

  // The Cancellations card's three windows (modules/cancellation-settings.js).
  loadCancellationSettings();

  // Empty string = "match my currency" — see saveDistanceUnitSetting.
  const distanceSelect = document.getElementById('s-distance-unit');
  if (distanceSelect) distanceSelect.value = settings.distanceUnit || '';

  const currencySelect = document.getElementById('s-currency');
  if (currencySelect) {
    currencySelect.innerHTML = CURRENCIES.map(c =>
      `<option value="${c.code}"${settings.currency === c.code ? ' selected' : ''}>${c.label}</option>`
    ).join('');
  }

  const dateFormatSelect = document.getElementById('s-date-format');
  if (dateFormatSelect) {
    dateFormatSelect.innerHTML = DATE_FORMATS.map(f =>
      `<option value="${f.value}"${settings.dateFormat === f.value ? ' selected' : ''}>${f.label}</option>`
    ).join('');
  }

  const unitSystem = settings.unitSystem || 'imperial';
  document.querySelectorAll('#unit-system-control .theme-seg-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.unitSystem === unitSystem);
  });

  // Retention settings
  const retApproach = document.getElementById('s-retention-default-approach');
  if (retApproach) {
    const val = settings.defaultSaveApproach || 'ask';
    retApproach.dataset.value = val;
    const labels = { ask: 'Ask me each time', call: 'Always start with a call', message: 'Send offers via message' };
    const textEl = retApproach.querySelector('.custom-select-text');
    if (textEl) textEl.textContent = labels[val] || labels.ask;
  }
  const retMaxDays = document.getElementById('s-retention-max-pause-days');
  if (retMaxDays) retMaxDays.value = settings.retentionMaxPauseDays || 90;
  const retMaxPauses = document.getElementById('s-retention-max-pauses-year');
  if (retMaxPauses) retMaxPauses.value = settings.retentionMaxPausesPerYear || 1;
  const retFollowups = document.getElementById('s-retention-followups-enabled');
  if (retFollowups) retFollowups.checked = settings.retentionFollowupsEnabled === true;
  const bookingLink = document.getElementById('s-churn-booking-link');
  if (bookingLink) bookingLink.value = settings.churnBookingLink || '';
  const agreementTpl = document.getElementById('s-cancel-agreement-template');
  if (agreementTpl && settings.cancelAgreementTemplate) agreementTpl.value = settings.cancelAgreementTemplate;
  renderCancelAgreementPreview(settings);
  // Change billing round two, piece 2a: with Change billing on, a client's cancel email starts their notice, so the
  // paragraph says so (cancel-agreement-language.js). Asked once; a box the coach is already editing is left alone.
  useChangeBillingParagraph({ box: agreementTpl, saved: settings.cancelAgreementTemplate || null,
    ask: () => callFunction('coach-billing-plan', null, 'GET', { account: 1 }) })
    .then((swapped) => { if (swapped) renderCancelAgreementPreview(loadSettings()); })
    .catch(() => {});
  // A cancellation address switched on in the background since this tab
  // loaded shows here without a reload (cron-email-domains-verify.js). Drawn
  // again whenever the answer comes back, not only when this call changed the
  // copy: the Email card asks at the same moment and may take the news first.
  refreshEmailDomainStatus().then(() => renderCancelAgreementPreview(loadSettings())).catch(() => {});

  // Update global reference for save attempt modal
  window._coachRetentionSettings = {
    defaultSaveApproach: settings.defaultSaveApproach || 'ask',
    retentionMaxPauseDays: settings.retentionMaxPauseDays || 90,
    retentionMaxPausesPerYear: settings.retentionMaxPausesPerYear || 1,
    churnBookingLink: settings.churnBookingLink || '',
  };

  // Render retention follow-up list (defined in app.js)
  window.renderRetentionFollowups?.();
  if (currentView === 'settings') window.loadCommsCardStatuses?.();

  // Quick replies (Messaging tab): the phone's list, from the server.
  void renderQuickRepliesCard();

  // Custom dropdowns — upgrade once, then re-read the options/values set
  // above (timezone/currency/date-format rebuild their option lists every
  // render; intensity/form-video/warn selects get their values set).
  const settingsView = document.getElementById('view-settings');
  upgradeSelects(settingsView);
  syncUpgradedSelects(settingsView);

  // Re-snapshot all save buttons now that fields are populated
  window.initSettingsDirtyTracking?.();

  // The remembered tab's own initialiser — moved here from five (plus a
  // sixth, App Studio) boot setTimeouts that fired regardless of which view
  // was being restored (see app.js, bindEvents, the comment above
  // _initActiveSettingsTab). Idempotent per tab, so re-entering Settings does
  // not re-fire it.
  //
  // ⚠️ GUARDED ON currentView, NOT JUST "renderSettings() ran". This function
  // is also called from _checkAutoTheme() (app.js) on an OS dark/light switch
  // or its 60s poll, regardless of which view is on screen — an unguarded
  // call here would put the exact boot-firing bug back, just triggered by a
  // theme change instead of a timer.
  if (currentView === 'settings') {
    window._initActiveSettingsTab?.();
    // Settings → Availability is painted by its own module (modules/availability-sets.js), not by app.js's
    // _initActiveSettingsTab; a click on its tab button is wired there too. Landing straight on it as the remembered tab
    // is this line. It reads the sets again each time, so the list is never older than the last time Settings opened.
    let remembered = '';
    try { remembered = localStorage.getItem('revfit_settings_tab') || ''; } catch { /* storage blocked: no remembered tab */ }
    if (remembered === 'availability') void initAvailabilitySets();
  }
}

/**
 * Paint the "Ready to paste" block under the agreement textarea.
 *
 * ⚠️ READS THE TEXTAREA, NOT THE SAVED SETTINGS, when the box has focus-level
 * edits in it. A preview of what the coach saved last week, sitting under the
 * sentence they are editing right now, is how somebody pastes the wrong
 * paragraph into a contract.
 *
 * @param {any} settings
 * @returns {void}
 */
export function renderCancelAgreementPreview(settings) {
  const out = document.getElementById('s-cancel-agreement-preview');
  const note = document.getElementById('s-cancel-agreement-note');
  if (!out) return;
  const s = settings || {};
  const box = document.getElementById('s-cancel-agreement-template');
  const template = (box && box.value) || s.cancelAgreementTemplate || DEFAULT_CANCEL_AGREEMENT_TEMPLATE;
  const cancelEmail = buildCancelAddress(s);
  out.textContent = renderCancelAgreement(template, {
    cancelEmail,
    coachName: s.coachName || s.emailBusinessName || '',
    businessName: s.emailBusinessName || s.businessName || '',
  });
  if (note) {
    // Colour is never the only signal — the sentence says which address it is.
    note.textContent = isBrandedCancelAddress(s)
      ? `Using your own address, ${cancelEmail}.`
      : `Using the FitOps Pro address, ${cancelEmail}. Finish the email setup in Settings, then Integrations, then the Email card, to put this on your own domain.`;
    note.classList.toggle('is-platform', !isBrandedCancelAddress(s));
  }
}

// ── Quick replies (Messaging tab; D9, design spec §2.10 and §14) ──
//
// ⚠️ THE COACH PHONE'S LIST, NOT A LIST OF ITS OWN. This card was "Quick Reply
// Templates" and kept its own list in the browser's saved settings
// (revfit_settings.chatTemplates, synced to user_config), which the phone
// never saw. It now reads and saves `coach-quick-replies` through
// messages/quick-replies.js: the one list the phone and the corner's Quick
// replies menu use. The server merged the old list in once
// (_shared/quick-replies-merge.js); nothing here writes chatTemplates again.
//
// ⚠️ EVERY ADD AND DELETE STARTS FROM THE SERVER'S LIST AS IT IS NOW. A save
// sends the whole list, and Settings can stay open for hours while the coach
// edits the list on the phone. A save built on the list this card drew when
// it opened would quietly delete what the phone added since. So each save
// asks for the list again, makes its one change to that, and sends it.
//
// ⚠️ A FAILED SAVE SAYS WHY AND LOSES NOTHING. The list shows what the server
// holds, the typed label and text stay in their boxes, and the server's
// sentence shows under them (a 5xx says only "Couldn't save your quick
// replies. Try again.", messages/quick-replies.js).
//
// Team members read the owner's list and change nothing: no boxes, no
// buttons, "Only the account owner can change these." The server refuses
// their save anyway (coach-quick-replies.js). Shown or hidden on every draw,
// never removed, because the page learns who is signed in after it loads.

/** Moves on every draw, so an older load never paints over a newer list. */
let _qrDraw = 0;
/** One save at a time: a second press while one runs adds nothing twice. */
let _qrSaving = false;

const QR_PREVIEW_CHARS = 120;
const QR_DELETE_ICON = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><polyline points="2,4 14,4"/><path d="M5 4V2h6v2"/><path d="M3 4l1 10h8l1-10"/></svg>';

const qrCanChange = () => !window._appMember;
// The phone editor's id shape (apps/mobile/app/coach/snippets.tsx newId).
const qrNewId = () => `r-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/** The line under the boxes: the server's words, or nothing. */
function qrSay(words) {
  const line = document.getElementById('s-qr-error');
  if (!line) return;
  line.textContent = words || '';
  line.hidden = !words;
}

function qrPaint(html) {
  const list = document.getElementById('s-quick-replies-list');
  _qrDraw += 1;
  if (list) list.innerHTML = html;
}

/** @param {{ id: string, label: string, body: string }[]} replies */
function qrDrawList(replies) {
  if (replies.length === 0) {
    qrPaint('<p class="settings-note" style="margin:0;">No quick replies yet.</p>');
    return;
  }
  const canChange = qrCanChange();
  qrPaint(replies.map((r) => {
    // Cut by characters, not UTF-16 units, so an emoji is never split in half.
    const chars = Array.from(String(r.body || ''));
    const start = chars.length > QR_PREVIEW_CHARS ? `${chars.slice(0, QR_PREVIEW_CHARS).join('')}…` : chars.join('');
    const del = canChange
      ? `<button type="button" class="s-rt-delete" title="Delete" aria-label="Delete ${escHtml(r.label || 'this reply')}" data-id="${escHtml(r.id)}">${QR_DELETE_ICON}</button>`
      : '';
    return `<div class="s-rt-item" data-id="${escHtml(r.id)}">
        <div class="s-rt-item-body">
          <div class="s-rt-item-name">${escHtml(r.label)}</div>
          <div class="s-rt-item-text">${escHtml(start)}</div>
        </div>
        ${del}
      </div>`;
  }).join(''));
}

/**
 * Ask for the list as the server holds it now, make one change, save the whole list.
 * @param {(current: { id: string, label: string, body: string }[]) => { id: string, label: string, body: string }[]} change
 * @param {() => void} onSaved
 */
async function qrChange(change, onSaved) {
  if (_qrSaving) return;
  _qrSaving = true;
  const addBtn = /** @type {HTMLButtonElement | null} */ (document.getElementById('btn-add-quick-reply'));
  if (addBtn) addBtn.disabled = true;
  qrSay('');
  try {
    let current;
    try {
      current = await loadQuickReplies({ force: true });
    } catch (err) {
      const status = Number(err?.status) || 0;
      qrSay(status >= 400 && status < 500 && err?.message ? err.message : SAVE_FAILED);
      return;
    }
    const result = await saveQuickReplies(change(current));
    if (!result.ok) {
      qrDrawList(current);
      qrSay(result.error);
      return;
    }
    qrDrawList(result.replies);
    onSaved();
  } finally {
    _qrSaving = false;
    if (addBtn) addBtn.disabled = false;
  }
}

function qrWire(list) {
  if (!list._qrWired) {
    list._qrWired = true;
    list.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      if (target.closest('[data-qr-retry]')) { void renderQuickRepliesCard(); return; }
      const del = /** @type {HTMLElement | null} */ (target.closest('.s-rt-delete'));
      if (!del) return;
      const id = del.dataset.id;
      void qrChange((current) => current.filter((r) => r.id !== id),
        () => showToast('Quick reply deleted.', 'success'));
    });
  }
  const addBtn = /** @type {(HTMLButtonElement & { _qrWired?: boolean }) | null} */ (document.getElementById('btn-add-quick-reply'));
  if (addBtn && !addBtn._qrWired) {
    addBtn._qrWired = true;
    addBtn.addEventListener('click', () => {
      if (_qrSaving) return;
      const nameBox = /** @type {HTMLInputElement | null} */ (document.getElementById('s-ct-name'));
      const textBox = /** @type {HTMLTextAreaElement | null} */ (document.getElementById('s-ct-text'));
      const label = (nameBox?.value || '').trim();
      const body = (textBox?.value || '').trim();
      if (!label) { qrSay('Enter a label.'); return; }
      if (!body) { qrSay('Enter the reply text.'); return; }
      void qrChange((current) => [...current, { id: qrNewId(), label, body }], () => {
        if (nameBox) nameBox.value = '';
        if (textBox) textBox.value = '';
        showToast('Quick reply added.', 'success');
      });
    });
  }
}

/**
 * Draw Settings → Messaging → Quick replies from the server's list.
 * Asked fresh on every draw (Settings is opened rarely; the phone may have
 * changed the list since this page loaded). Never touches what is typed in
 * the boxes, so a Settings repaint loses nothing.
 * @returns {Promise<void>}
 */
export function renderQuickRepliesCard() {
  const list = document.getElementById('s-quick-replies-list');
  if (!list) return Promise.resolve();
  const canChange = qrCanChange();
  const add = document.getElementById('s-qr-add');
  if (add) add.hidden = !canChange;
  const note = document.getElementById('s-qr-note');
  if (note) note.hidden = !canChange;
  const ownerOnly = document.getElementById('s-qr-owner-only');
  if (ownerOnly) ownerOnly.hidden = canChange;
  qrWire(list);
  const draw = ++_qrDraw;
  return loadQuickReplies({ force: true }).then((replies) => {
    if (draw !== _qrDraw) return;
    qrDrawList(replies);
  }, (err) => {
    if (draw !== _qrDraw) return;
    qrPaint(`<p class="settings-note" style="margin:0;">${escHtml(err?.message || LOAD_FAILED)}</p>
      <button type="button" class="btn-secondary" data-qr-retry style="margin-top:8px;">Try again</button>`);
  });
}

export function renderTagSettings() {
  const list = document.getElementById('tag-defs-list');
  if (!list) return;
  const defs = loadTagDefs();
  list.innerHTML = defs.length === 0
    ? `<p class="settings-note" style="margin:0">No tags yet. Add one below.</p>`
    : defs.map((d, i) => {
        const { bg, color, border } = getTagStyle(d.color);
        return `<div class="tag-def-item">
          <input type="color" class="tag-def-color-picker" value="${d.color}" title="Change color"
            onchange="window.updateTagDefColor(${i}, this.value)" />
          <span class="tag-chip tag-def-chip" id="tag-def-chip-${i}" style="background:${bg};color:${color};border-color:${border}">${escHtml(d.name)}</span>
          <button class="tag-def-remove" onclick="window.removeTagDef(${i})" title="Delete tag">×</button>
        </div>`;
      }).join('');
}

// ── Settings → Integrations → Your domain ───────────────────────
// One card for every DNS record the platform uses; the markup and every
// interaction live in your-domain.js, loaded only when Settings renders. It
// draws the Booking links card too (booking-domain-wizard.js), from the one
// plan it reads, which is by the account, never the login: the card's Save
// acts on the account (coach-handle-set), so it shows the account's handle.
// A card that cannot load must not take the rest of Settings down with it.
async function renderYourDomainCard() {
  try {
    const { renderYourDomain } = await import('./your-domain.js');
    await renderYourDomain();
  } catch (e) {
    console.warn('[settings] your domain load failed:', e?.message);
  }
}

export function exportCSV() {
  const headers = [
    'First Name', 'Last Name', 'Phone', 'Email', 'Date of Birth',
    'Address Line 1', 'Address Line 2', 'City', 'State', 'Zip Code',
    'Status', 'Service Type', 'Billing', 'Payment Source',
    'Sign Up Date', 'Program Start Date', 'Agreement Length (mo)', 'Bonus Months',
    'Agreement Value', 'Collected Upfront', 'MRR Amount', 'Installment Count', 'Continuity Rate',
    'Months as Client', 'End Date', 'Renewal Needed', 'Inactive Date',
    'Cancellation Reason', 'Tags', 'Notes',
    'Stripe Customer ID', 'Stripe Subscription ID'
  ];

  const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;

  const rows = clients.map(c => {
    const endDate = getEndDate(c);
    return [
      c.firstName || '', c.lastName || '', c.phone || '', c.email || '', c.birthdate || '',
      c.addressLine1 || c.streetAddress || '', c.addressLine2 || '',
      c.city || '', c.state || '', c.zipCode || '',
      c.status, c.serviceType, c.billingStructure, c.paymentSource,
      c.signUpDate || '', c.programStartDate || '',
      c.lengthOfAgreement || 0, c.bonusMonths || 0,
      c.agreementValue || '', c.moneyCollectedUpfront || '',
      c.mrrAmount || '', c.installmentCount || '', continuityRateOf(c) || '',
      getMonthsAsClient(c),
      endDate ? localDateStr(endDate) : '',
      isRenewalNeeded(c) ? 'Yes' : 'No',
      c.inactiveDate || '', c.cancellationReason || '',
      (c.tags || []).join('; '), c.notes || '',
      c.stripeCustomerId || '', c.stripeSubscriptionId || ''
    ].map(escape).join(',');
  });

  const csv  = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = `fitops-pro-clients-${localToday()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function renderColumnPanel() {
  const panel = document.getElementById('column-panel');
  if (!panel) return;
  const toggleable = COLUMNS.filter(col => !col.alwaysVisible);
  panel.innerHTML = `
    <div class="col-panel-header"><span>Show / hide columns</span><button class="col-panel-reset" id="btn-reset-columns">Reset</button></div>
    <div class="col-panel-list">
      ${toggleable.map(col => `
        <label class="col-panel-item">
          <input type="checkbox" data-colkey="${col.key}" ${visibleColumns[col.key] ? 'checked' : ''} />
          <span>${col.label}</span>
        </label>
      `).join('')}
    </div>`;

  panel.querySelectorAll('input[type=checkbox]').forEach(cb => {
    cb.addEventListener('change', () => {
      visibleColumns[cb.dataset.colkey] = cb.checked;
      saveColumnPrefs(visibleColumns);
      if (_renderClientsTable) _renderClientsTable();
    });
  });

  document.getElementById('btn-reset-columns')?.addEventListener('click', () => {
    COLUMNS.forEach(col => {
      if (!col.alwaysVisible) visibleColumns[col.key] = col.defaultVisible !== false;
    });
    saveColumnPrefs(visibleColumns);
    renderColumnPanel();
    if (_renderClientsTable) _renderClientsTable();
  });
}

// Sidebar has two fixed states only — expanded (220px) / collapsed (72px).
// The old drag-to-resize was never used, so it's gone; this just restores the
// saved collapsed state on load. (Name kept for the existing import/call site.)
export function initSidebarResize() {
  applySidebarCollapsed(savedSidebarCollapsed());
}
